import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template del **Compagno Animale del Ranger** (manuale V1, rr. 5234-5258).
 *
 * Pattern stress-testati a beneficio dello schema FI-8a:
 *  - **`hpRule: scaling`** con max (1 + 2×lvl, cap 21)
 *  - **modifier-bonus su sottoinsieme di stat** (4 di 6: FOR/DES/COS/SAG;
 *    NO INT, NO CAR — manuale r. 5242)
 *  - **`capabilityCaps`** (d10 max al dado, +2 max all'Armatura, +6 max al
 *    Carico — manuale r. 5254)
 *  - **archetype da `ChoiceGroup`** (specie scelta tra 11 opzioni)
 *  - **trait da `ChoiceGroup`** (capacità utile iniziale: feroce / tenace /
 *    resistente)
 *  - **nome da `NarrativeField`** ("Nome del Compagno Animale")
 *  - **0 Abilità preset** (il Compagno non è un gregario con "mestiere"; le
 *    sue capacità sono i `traits` modulari)
 *
 * Pattern fuori scope FI-8a (description-only, integrati nella mossa-source):
 *  - opzione "altro tipo di animale concordato con GM" (specie aperta) →
 *    description-only nel testo della mossa
 *  - cap d10 / +2 / +6 considerati ai successivi upgrade via *Ben Addestrato*
 *    (mossa advanced L2-5, fuori scope L1)
 *  - bonus "+1 al tiro quando collabori col Compagno" → è una **regola di
 *    gioco** della mossa-source, non un effetto del Follower
 */
export const rangerCompagnoAnimaleTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'ranger-compagno-animale',
  sourceMoveId: 'ranger.compagno-animale',
  type: 'animal-companion',
  label: 'Compagno Animale',
  baseStats: {
    level: 2,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'scaling', base: 1, perCharacterLevel: 2, max: 21 },
    damageDie: 'd6',
    load: 6,
    tags: [],
  },
  fixedSkills: [],
  fixedTraits: [],
  modifierBonus: {
    fromChoiceGroup: 'modificatore',
    amount: 2,
  },
  archetypeFromChoiceGroup: 'specie',
  traitsFromChoiceGroup: {
    fromChoiceGroup: 'capacita',
    optionToTrait: {
      feroce: {
        id: 'feroce',
        label: 'Feroce',
        description: 'Il dado di Danno aumenta di una categoria (max d10).',
        effects: { damageDieOverride: 'd8' },
      },
      tenace: {
        id: 'tenace',
        label: 'Tenace',
        description: '+1 Armatura (max +2 con upgrade successivi).',
        effects: { armorDelta: 1 },
      },
      resistente: {
        id: 'resistente',
        label: 'Resistente',
        description: '+3 Carico (max +6 con upgrade successivi).',
        effects: { loadDelta: 3 },
      },
    },
  },
  nameFromNarrativeField: 'nome-compagno',
  archetypeCustomFromNarrativeField: {
    fromChoiceGroup: 'specie',
    whenChoice: 'altro',
    narrativeFieldId: 'specie-custom',
  },
  traitCustomFromNarrativeField: {
    fromChoiceGroup: 'capacita',
    whenChoice: 'altro',
    labelFieldId: 'capacita-custom-label',
    descriptionFieldId: 'capacita-custom-description',
  },
  capabilityCaps: { damageDieMax: 'd10', armorMax: 2, loadMax: 12 },
  respawnRule: 'Nuovo legame con altro animale (GM decide quando trovi il compagno adatto).',
  // D8c — FI-41: chain advanced (Ben Addestrato L2-5 + Alleato Innaturale
  // L6-10). Le mosse del PG (`ranger.ben-addestrato`, `ranger.alleato-innaturale`)
  // dichiarano i propri `narrativeFields` (vedi `data/moves/ranger/advanced-*.ts`).
  // Il template mappa le entry a:
  //  - Ben Addestrato: +2 capacità utili custom sempre presenti
  //  - Alleato Innaturale: level 2→3 + mod +2→+3 + 2 capacità custom sempre
  //    presenti (+ descrizione metamorfosi narrativa già esistente FI-18)
  upgrades: [
    {
      fromMoveId: 'ranger.ben-addestrato',
      additionalAlwaysTraitsFromNarrativeFields: [
        {
          traitId: 'ben-addestrato-cap1',
          labelFieldId: 'ba-cap1-label',
          descriptionFieldId: 'ba-cap1-desc',
        },
        {
          traitId: 'ben-addestrato-cap2',
          labelFieldId: 'ba-cap2-label',
          descriptionFieldId: 'ba-cap2-desc',
        },
      ],
    },
    {
      fromMoveId: 'ranger.alleato-innaturale',
      levelOverride: 3,
      modifierBonusOverride: { fromChoiceGroup: 'modificatore', amount: 3 },
      additionalAlwaysTraitsFromNarrativeFields: [
        {
          traitId: 'alleato-innaturale-cap1',
          labelFieldId: 'ai-cap1-label',
          descriptionFieldId: 'ai-cap1-desc',
        },
        {
          traitId: 'alleato-innaturale-cap2',
          labelFieldId: 'ai-cap2-label',
          descriptionFieldId: 'ai-cap2-desc',
        },
      ],
    },
  ],
});
